import React, { FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import {
  useRunReportQuery,
  useGetReportSpecificFieldsQuery,
} from "../../../api/network/reportApiServices";
import { TModalsState } from "../../../api/store/commonSlice";
import svgIcons from "../../../assets/svg/svgIcons";
import { APP_CONFIG } from "../../../constants/constants";
import { jsTimeToDBTime } from "../../../utils/dateTime";
import ModalSwitchChartType from "../components/modalSwitchChartType";
import { IWidgetProps } from "../components/overviewDragDropColumnItem";
import PillSelector from "../components/pillSelector";
import WidgetChartAreaGenerator from "../components/widgetChartAreaGenerator";
import WidgetFilterSelector from "../components/widgetFilterSelector";
import { useOverviewContext } from "../context/overview.context";
import { EWidgetChartTypes } from "../types/overview.types";
import { verticalLabelFormatter } from "../utils/chart";
import { useGroupsAndAssetsUtils } from "../hooks/overviewScreenUtils";

const data = [
  { name: "Group A", value: 400 },
  { name: "Group B", value: 300 },
  { name: "Group C", value: 300 },
  { name: "Group D", value: 200 },
];
// prettier-ignore
const countFields = ['471', '472', '473', '474', '475', '790', '476', '477', '478', '479', '480', '481', '482', '483', '484', '3010', '3011', '3012', '3013', '3014', '3015', '3016', '3017'];
// prettier-ignore
const countTotalField = '485';
// prettier-ignore
const scoreFields = ['486', '487', '488', '489', '490', '789', '491', '492', '493', '494', '495', '496', '497', '498', '499', '3000', '3001', '3002', '3003', '3004', '3005', '3006', '3007'];
const scoreTotalField = "500";

const graphXAxisDataKey = "title";
const graphYAxisDataKey = "value";

const defaultChartType = EWidgetChartTypes.DOUGHNUT;

const WidgetSafetyEvents: FC<IWidgetProps> = ({ widgetType }) => {
  const { t: tOverview } = useTranslation("translation", {
    keyPrefix: "overviewScreen",
  });
  const { t } = useTranslation("translation", {
    keyPrefix: "overviewScreen.widgets.enum_4",
  });

  const modalsState = useSelector(
    (state: { commonReducer: { modals: TModalsState } }) =>
      state.commonReducer.modals
  );
  const [selectedChartType, setSelectedChartType] = useState(defaultChartType);

  const pills = ["Score", "Count"];
  const [selectedPill, setSelectedPill] = React.useState(pills[0]);

  const reportId = selectedPill === "Score" ? 78 : 106;

  const {
    allTimeFilters,
    timeFilter,
    allGroupsSelected,
    selectedGroups,
    selectedDataType,
    setSelectedDataType,
    allAssetsSelected,
    selectedAssets,
    setAllAssetsSelected,
    setSelectedAssets,
  } = useOverviewContext();

  const {
    data: dataSpecificReport,
    isFetching: isFetchingSpecificReport,
    isUninitialized: isUninitializedSpecificReport,
  } = useGetReportSpecificFieldsQuery(
    {
      // type: REPORT_TYPES.CUSTOM,
      report_id: reportId,
    },
    { refetchOnMountOrArgChange: true }
  );

  // const { data: dataReport, isFetching } = useRunReportQuery(
  //   {
  //     id: reportId.toString(),
  //     // fields: '443,444,447'.split(','),
  //     // ordering: '447',

  //     page: 1,
  //     page_size: 10,

  //     // filters
  //     device_groups:
  //       allGroupsSelected === false && !!selectedGroups
  //         ? selectedGroups
  //         : undefined,
  //     start_time: !!allTimeFilters.find((filter) => filter.slug === timeFilter)
  //       ?.timeRange[0]
  //       ? jsTimeToDBTime(
  //           allTimeFilters
  //             .find((filter) => filter.slug === timeFilter)
  //             ?.timeRange[0]?.getTime() ?? 0
  //         )
  //       : undefined,
  //     end_time: !!allTimeFilters.find((filter) => filter.slug === timeFilter)
  //       ?.timeRange[1]
  //       ? jsTimeToDBTime(
  //           allTimeFilters
  //             .find((filter) => filter.slug === timeFilter)
  //             ?.timeRange[1]?.getTime() ?? 0
  //         )
  //       : undefined,
  //     ds_time_range: allTimeFilters.find((filter) => filter.slug === timeFilter)
  //       ?.value
  //   },
  //   {
  //     skip: isUninitializedSpecificReport || isFetchingSpecificReport,
  //     refetchOnMountOrArgChange: true
  //   }
  // );

  const {
    reportData: dataReport,
    isFetching,
    isError,
    reportError,
  } = useGroupsAndAssetsUtils(selectedDataType, reportId, selectedAssets);

  const [computedChartData, setComputedChartData] = useState<any[]>([]);
  useEffect(() => {
    if (
      !!dataReport &&
      !!dataReport?.results &&
      !!dataSpecificReport?.report_fields &&
      !!dataReport?.results?.[0]
    ) {
      setComputedChartData(
        Object.keys(dataReport?.results?.[0])
          // filtering our values based on the selected pill
          .filter((item) =>
            selectedPill === "Count"
              ? countFields.includes(item)
              : scoreFields.includes(item)
          )
          // mapping the data to the chart data format
          .map((columnKeys) => ({
            [graphXAxisDataKey]:
              dataSpecificReport?.report_fields?.find(
                (field) => field.field.id === parseInt(columnKeys)
              )?.field?.name +
              (APP_CONFIG.DEBUG.DISPLAY_VERBOSE ? ` (${columnKeys})` : ""),
            [graphYAxisDataKey]: !!dataReport?.results?.[0][columnKeys]
              ? dataReport?.results?.[0][columnKeys]
              : 0,
          }))
      );
    }
  }, [dataReport, dataSpecificReport, selectedPill]);

  const totalCount =
    !!dataReport && !!dataReport?.results
      ? dataReport?.results?.[0]?.[
          selectedPill === "Count" ? countTotalField : scoreTotalField
        ]
      : 0;

  return (
    <div className="mt-2">
      {APP_CONFIG.DEBUG.DISPLAY_VERBOSE && (
        <pre className="absolute text-[8px] text-field-error-dark -mt-2">
          reportId: {reportId}
        </pre>
      )}

      {isFetching ? (
        <div className="h-48 relative">
          <img
            src={svgIcons.LoadingAnimation}
            alt="laoding"
            className="absolute size-8 top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2"
          />
        </div>
      ) : !!dataReport &&
        !!dataReport?.results?.length &&
        dataReport?.results?.length > 0 ? (
        <>
          {/* Filter */}
          <div className="mb-4 flex flex-row-reverse justify-between">
            <PillSelector
              pills={pills}
              selectedPill={selectedPill}
              setSelectedPill={setSelectedPill}
            />
            <WidgetFilterSelector
              selectedFilter={t("filter_label")}
              filter={""}
              // setFilter={() => { }}
            />
          </div>

          <WidgetChartAreaGenerator
            barChartMargin={
              selectedChartType === EWidgetChartTypes.HORIZONTAL_BAR_GRAPH
                ? { top: 0, right: 10, bottom: 0, left: -20 }
                : { top: 0, right: 10, bottom: 20, left: -30 }
            }
            cellItemsForSectionColor={
              !!dataReport &&
              !!dataReport?.results &&
              !!dataSpecificReport?.report_fields &&
              !!dataReport?.results?.[0]
                ? Object.keys(dataReport?.results?.[0])
                : []
            }
            chartHighlightLabel={t(
              selectedPill === "Count"
                ? "label_total_count"
                : "label_total_score"
            )}
            chartHighlightValue={
              !totalCount || totalCount === 0
                ? "0"
                : verticalLabelFormatter(totalCount)
            }
            chartType={selectedChartType}
            chartWrapperHeightClass={
              selectedChartType === EWidgetChartTypes.HORIZONTAL_BAR_GRAPH
                ? "pt-[100%]"
                : "pt-[45%]"
            }
            computedChartData={
              !!dataReport &&
              !!dataReport?.results &&
              !!dataSpecificReport?.report_fields
                ? computedChartData?.sort(
                    (a, b) => b[graphYAxisDataKey] - a[graphYAxisDataKey]
                  )
                : []
            }
            graphXAxisDataKey={graphXAxisDataKey}
            graphYAxisDataKey={graphYAxisDataKey}
            legendLabelFormatter={(value: any) => value}
            legendVerticalAlignClass="justify-start"
            t={t}
            useLegendValueFormatter={true}
            widgetType={widgetType}
            xAxisTickLabelWidth={40}
            xAxisTickUsesTranslation={false}
            yAxisTickFormatter={(value) =>
              value === 0 ? "0" : verticalLabelFormatter(value)
            }
          />

          {/* Legend */}
          {/* <div className="flex-1 flex items-center relative">
              <div className="absolute w-full h-full flex flex-col items-start gap-2 pr-2 overflow-auto">
                {
                  !!dataReport && !!dataReport?.results && !!dataSpecificReport?.report_fields
                    ? computedChartData
                      ?.sort((a, b) => b[graphYAxisDataKey] - a[graphYAxisDataKey])
                      // .filter((item) => item[graphXAxisDataKey] !== 'total_count')
                      .map((entry, index) => (
                        <div key={index} className="w-full flex items-center justify-between gap-2 mb-1 text-xs">
                          <div className="flex items-center">
                            <div className="shrink-0 size-3 rounded-sm mr-2" style={{ backgroundColor: chartColors[index % chartColors.length] }}></div>
                            <span className="flex-grow break-words leading-3">{entry.title}</span>
                          </div>
                          <span className="ml-2 font-bold text-heading-gray666" title={entry.value}>
                            {entry.value === 0 ? '0' : verticalLabelFormatter(entry.value)}
                          </span>
                        </div>
                      ))
                    : []
                }
              </div>
            </div> */}

          {/* Pie Chart */}
          {/* <div className="flex-1 pt-[50%] relative">
              <SwitchChartButton onClick={() => {
                dispatch(setModalsData({ ...modalsState, switchChartTypeForWidget: widgetType, showSwitchChartType: true }))
              }} />
              <ResponsiveContainer className={'absolute top-0 left-0 w-full h-full'}>
                <PieChart width={pieSize} height={pieSize} onMouseEnter={undefined}>
                  <Pie
                    data={
                      !!dataReport && !!dataReport?.results && !!dataSpecificReport?.report_fields
                        ? computedChartData
                          ?.sort((a, b) => b[graphYAxisDataKey] - a[graphYAxisDataKey])
                        // .filter((item) => item[graphXAxisDataKey] !== 'total_count')
                        : []
                    }
                    cx={"50%"}
                    cy={"50%"}
                    innerRadius={"75%"} // {pieSize - 35}
                    outerRadius={"95%"}// {pieSize - 20}
                    fill="#8884d8"
                    // paddingAngle={5}
                    dataKey="value"
                  >
                    {
                      !!dataReport && !!dataReport?.results && !!dataSpecificReport?.report_fields && !!dataReport?.results?.[0] &&
                      Object.keys(dataReport?.results?.[0])
                        .map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={chartColors[index % chartColors.length]} />
                        ))
                    }
                  </Pie>
                  <foreignObject
                    // x={"0%"}
                    // y={"42%"}
                    width={"100%"}
                    height={"100%"}
                  >
                    <div className="flex flex-col items-center justify-center w-full h-full">
                      <p className="text-center leading-[0.8vw] text-[0.7vw] text-heading-gray666 px-[20%] pt-[0.5vw]">
                        {t(selectedPill === 'Count' ? 'label_total_count' : 'label_total_score')}
                      </p>
                      <p className="text-[1.5vw] leading-[1.5vw] font-bold text-center text-heading-gray222 mt-[0.6vw]">
                        {
                          !totalCount || totalCount === 0 ? '0' : verticalLabelFormatter(totalCount)
                        }
                      </p>
                    </div>
                  </foreignObject>
                </PieChart>
              </ResponsiveContainer>
            </div> */}
        </>
      ) : (
        <p className="p-6 text-center text-sm text-heading-gray888">
          {tOverview("no_widget_data")}
        </p>
      )}
      {modalsState.showSwitchChartType &&
        modalsState.switchChartTypeForWidget === widgetType && (
          <ModalSwitchChartType
            selectedChartType={selectedChartType}
            onChartTypeChange={(newChartType) => {
              setSelectedChartType(newChartType);
            }}
          />
        )}
    </div>
  );
};
export default WidgetSafetyEvents;
